import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import BottomNav from './BottomNav';
import DesktopSidebar from './DesktopSidebar';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppShell({ children, active }) {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(false);
  const [score, setScore] = useState(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('gcf_sidebar_collapsed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1200);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const handler = () => {
      try { setSidebarCollapsed(localStorage.getItem('gcf_sidebar_collapsed') === 'true'); } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const fetchScore = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard`, { withCredentials: true });
      if (res.data?.score?.total != null) setScore(res.data.score.total);
    } catch {}
  }, []);

  useEffect(() => { fetchScore(); }, [fetchScore]);

  useEffect(() => {
    axios.get(`${API}/alerts`, { withCredentials: true })
      .then(res => setUnreadAlerts(res.data.unread_count || 0))
      .catch(() => {});
  }, []);

  const sidebarWidth = isDesktop ? (sidebarCollapsed ? 64 : 220) : 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      {isDesktop && <DesktopSidebar score={score} />}

      <div
        className="transition-[margin] duration-200 ease-out relative"
        style={{ marginLeft: sidebarWidth }}
      >
        {/* Mobile top bar */}
        {!isDesktop && (
          <div
            className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4"
            style={{ height: 48, backgroundColor: '#EDE8DF', borderBottom: '1px solid #D4C9B8' }}
            data-testid="mobile-top-bar"
          >
            <span className="text-[15px] font-semibold" style={{ color: '#3C3226', fontFamily: "'DM Sans', sans-serif" }}>GetChosenFirst</span>
            <button
              onClick={() => navigate('/alerts')}
              className="relative w-9 h-9 flex items-center justify-center"
              data-testid="alert-bell-btn"
            >
              <Bell size={18} strokeWidth={1.5} color="#8C7E6E" />
              {unreadAlerts > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[#F0EBE3] text-[9px] font-bold flex items-center justify-center"
                  style={{ backgroundColor: '#C67A5C' }}
                  data-testid="alert-badge"
                >
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Desktop alert bell */}
        {isDesktop && (
          <button
            onClick={() => navigate('/alerts')}
            className="fixed top-3 right-4 z-50 w-9 h-9 flex items-center justify-center transition-colors"
            style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8', borderRadius: '2px' }}
            data-testid="alert-bell-btn"
          >
            <Bell size={16} strokeWidth={1.5} color="#8C7E6E" />
            {unreadAlerts > 0 && (
              <span
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[#F0EBE3] text-[9px] font-bold flex items-center justify-center"
                style={{ backgroundColor: '#C67A5C' }}
                data-testid="alert-badge"
              >
                {unreadAlerts > 9 ? '9+' : unreadAlerts}
              </span>
            )}
          </button>
        )}

        <div style={{ paddingTop: !isDesktop ? 48 : 0 }}>
          {children}
        </div>
      </div>

      {!isDesktop && <BottomNav active={active} />}
    </div>
  );
}
